/**
 * Anchor-watch layer — the hook, the boundary the drag alarm watches (either the
 * swing circle or the custom-drawn zone), and the rolling swing track of where
 * the boat has actually sat. Sits on top of the chart with the other safety
 * geometry.
 *
 * Fed straight from the anchor-watch store so what's drawn and what the alarm
 * checks are the same numbers: the swing circle uses swingRadiusNm, the zone is
 * the stored polygon, and the whole thing turns to the alarm colour the moment
 * the boat is dragging, so a drag reads at a glance on the chart, not just on the
 * banner.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import { swingCircle } from '@/safety';
import { zoneBoundaryRing, edgeMidpoint } from '@/safety/anchorZone';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'anchor-watch';

export interface AnchorWatchLayerInput {
  anchor: [number, number] | null;
  mode: 'radius' | 'zone';
  /** Swing radius (nm) for the circle boundary in radius mode. */
  radiusNm: number;
  /** Custom boundary polygon [lon,lat] for zone mode. */
  zone: [number, number][];
  /** Where the boat has sat since the drop, for the swing-track trail. */
  swingTrack: [number, number][];
  /** True when the drag alarm is standing, so the boundary shows the alarm colour. */
  dragging: boolean;
}

function build(input: AnchorWatchLayerInput): FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  if (input.anchor) {
    // The boundary: swing circle or the drawn zone.
    if (input.mode === 'zone') {
      if (input.zone.length >= 2) {
        // The CURVE through the points, which is also what the drag alarm tests
        // against. Drawing the straight lines instead would show a boundary
        // tighter than the one being watched, and the gap is where a boat sits
        // apparently inside its ring with the alarm sounding.
        const ring =
          input.zone.length >= 3 ? zoneBoundaryRing(input.zone) : [...input.zone];
        features.push({
          type: 'Feature',
          geometry:
            input.zone.length >= 3
              ? { type: 'Polygon', coordinates: [ring] }
              : { type: 'LineString', coordinates: ring },
          properties: { kind: 'anchor-zone', dragging: input.dragging },
        });
      }
      // The vertices as grabbable dots, each carrying its index so a drag knows
      // which point it has hold of. A half-drawn zone still shows its points.
      input.zone.forEach((v, index) => {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: v },
          properties: { kind: 'anchor-zone-vertex', index },
        });
      });
      // A faint handle partway along every span. Pressing one splits that span,
      // the way a waypoint goes into a route. Showing them beats a hidden
      // long-press gesture: nobody discovers a gesture nothing hints at, and a
      // boundary you cannot see how to edit may as well not be editable.
      //
      // It sits on the CURVE rather than halfway along the straight line, or it
      // would float off the boundary it claims to be part of.
      if (input.zone.length >= 3) {
        input.zone.forEach((_, index) => {
          const m = edgeMidpoint(input.zone, index);
          if (!m) return;
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: m },
            properties: { kind: 'anchor-zone-midpoint', index },
          });
        });
      }
    } else if (input.radiusNm > 0) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [swingCircle(input.anchor, input.radiusNm)] },
        properties: { kind: 'anchor-swing', dragging: input.dragging },
      });
    }
    if (input.swingTrack.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: input.swingTrack },
        properties: { kind: 'anchor-track' },
      });
    }
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: input.anchor },
      properties: { kind: 'anchor-hook' },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function ensureAnchorWatchLayer(
  map: MaplibreMap,
  input: AnchorWatchLayerInput,
  palette: Palette,
): void {
  const p = getPalette(palette);
  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data: build(input) });
  } else {
    (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(build(input));
  }

  if (!map.getLayer('anchor-swing-fill')) {
    map.addLayer({
      id: 'anchor-swing-fill',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['in', ['get', 'kind'], ['literal', ['anchor-swing', 'anchor-zone']]],
      paint: { 'fill-color': p.UI_ACCENT, 'fill-opacity': 0.08 },
    });
  }
  if (!map.getLayer('anchor-swing-stroke')) {
    map.addLayer({
      id: 'anchor-swing-stroke',
      type: 'line',
      source: SOURCE_ID,
      filter: ['in', ['get', 'kind'], ['literal', ['anchor-swing', 'anchor-zone']]],
      paint: {
        'line-color': ['case', ['get', 'dragging'], p.CHMGD, p.UI_ACCENT] as never,
        'line-width': 1.4,
        'line-dasharray': [3, 2],
      },
    });
  }
  // The two kinds of handle used to be near enough the same size and weight, so
  // the ring read as one long run of beads and the real handle count looked like
  // double what it was (fb-msdph9hi-1). The points we drag are now the solid
  // ones and clearly bigger; the add-a-point handles are small and faint, there
  // to be found rather than to be counted.
  if (!map.getLayer('anchor-zone-vertex')) {
    map.addLayer({
      id: 'anchor-zone-vertex',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-zone-vertex'],
      paint: {
        'circle-radius': 5.5,
        'circle-color': p.UI_ACCENT,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 1.6,
      },
    });
  }
  if (!map.getLayer('anchor-zone-midpoint')) {
    map.addLayer({
      id: 'anchor-zone-midpoint',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-zone-midpoint'],
      paint: {
        'circle-radius': 2.6,
        'circle-color': p.UI_BG,
        'circle-opacity': 0.75,
        'circle-stroke-width': 1,
        'circle-stroke-color': p.UI_ACCENT ?? '#c62828',
        'circle-stroke-opacity': 0.6,
      },
    });
  }
  if (!map.getLayer('anchor-track')) {
    map.addLayer({
      id: 'anchor-track',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-track'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 1, 'line-opacity': 0.5 },
    });
  }
  if (!map.getLayer('anchor-hook')) {
    map.addLayer({
      id: 'anchor-hook',
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'anchor-hook'],
      // A drawn icon, not the character U+2693: the glyph stack has no such
      // character, so the anchor simply did not appear on the chart. See
      // 'plot-anchor' in icons.ts.
      layout: { 'icon-image': 'plot-anchor', 'icon-size': 0.85, 'icon-allow-overlap': true },
    });
  }
}

/** The anchor layers, bottom to top, for creation and for re-raising alike. */
const ANCHOR_LAYER_IDS = [
  'anchor-swing-fill',
  'anchor-swing-stroke',
  'anchor-zone-vertex',
  'anchor-zone-midpoint',
  'anchor-track',
  'anchor-hook',
] as const;

/**
 * Lift the anchor-watch layers back above the chart, the same medicine as
 * raiseOwnshipLayers and for the same disease: an on-demand vector cell arriving
 * after these layers appends its opaque land and depth fills ON TOP, and at
 * anchor that buried the swing circle and the hook (fb-msie4sj8-0, the exact
 * moment the circle matters most). moveLayer with no `before` sends a layer to
 * the top, so walking bottom to top preserves the internal order.
 */
export function raiseAnchorWatchLayers(map: MaplibreMap): void {
  for (const id of ANCHOR_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

export function updateAnchorWatchLayer(map: MaplibreMap, input: AnchorWatchLayerInput): void {
  const src = map.getSource(SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(build(input));
}
